import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTimeAgo } from "../../../../../../hooks/use-time-ago";
import {
	blockCommentUserRequest,
	markCommentReadRequest,
	replyToCommentRequest,
	shareCommentToFeedRequest,
} from "../../../../../../redux/broadcast/broadcast.actions";
import { setOpenMenu } from "../../../../../../redux/menus/actions";
import { selectorOpenMenuId } from "../../../../../../redux/menus/selectors";
import { showModal } from "../../../../../../redux/modals/actions";
import type { AuthorComment } from "../../../../../../types/comments.types";
import type { ChatStatuses } from "../chat-module.types";
import {
	ARCHIVED_COMMENT_ACTION_OPTIONS,
	DEFAULT_COMMENT_ACTION_OPTIONS,
	SHARED_ACTION_OPTIONS,
} from "./comment.options";

type UseCommentArgs = {
	comment: AuthorComment;
	broadcastId: string;
	status: ChatStatuses;
	// The viewer's own read-only comment list reuses this component purely for
	// display — none of the author moderation actions (reply/share/read/block)
	// make sense there, so this suppresses the menu entirely.
	readOnly?: boolean;
};

export const useComment = ({
	comment,
	broadcastId,
	status,
	readOnly,
}: UseCommentArgs) => {
	const dispatch = useDispatch();
	const [showReplyBox, setShowReplyBox] = useState(false);
	const [replyText, setReplyText] = useState("");
	const commentTimeAgo = useTimeAgo(comment.date);
	const replyTimeAgo = useTimeAgo(comment.repliedAt ?? comment.date);

	const openMenuId = useSelector(selectorOpenMenuId);
	const isMenuOpen = openMenuId === comment.id;
	const menuRef = useRef<HTMLDivElement>(null);

	const handleReply = useCallback(() => {
		const trimmed = replyText?.trim();
		if (!trimmed) return;

		dispatch(
			replyToCommentRequest({
				broadcastId,
				commentId: comment.id,
				reply: trimmed,
			}),
		);
		setReplyText("");
		setShowReplyBox(false);
	}, [dispatch, broadcastId, comment.id, replyText]);

	const handleShareToFeed = useCallback(() => {
		dispatch(shareCommentToFeedRequest({ broadcastId, commentId: comment.id }));
	}, [dispatch, broadcastId, comment.id]);

	const handleToggleMenu = useCallback(() => {
		dispatch(setOpenMenu(isMenuOpen ? null : comment.id));
	}, [dispatch, isMenuOpen, comment.id]);

	const handleMarkAsRead = useCallback(() => {
		if (comment.read) return;
		dispatch(markCommentReadRequest({ broadcastId, commentId: comment.id }));
	}, [dispatch, broadcastId, comment.id, comment.read]);

	const handleMarkAsUnread = useCallback(() => {
		dispatch(
			markCommentReadRequest({
				broadcastId,
				commentId: comment.id,
				read: false,
			}),
		);
	}, [dispatch, broadcastId, comment.id]);

	const menuOptions = useMemo(() => {
		if (readOnly) {
			return [];
		}

		if (status === "archived") {
			// Already replied while archived — marking it unread would just
			// re-surface something you've already dealt with.
			return ARCHIVED_COMMENT_ACTION_OPTIONS.filter(
				(option) => option.id !== "unread" || !comment.reply,
			);
		}

		if (status === "shared") {
			// Already replied while shared — no point offering to reply again.
			return SHARED_ACTION_OPTIONS.filter(
				(option) => option.id !== "reply" || !comment.reply,
			);
		}

		return DEFAULT_COMMENT_ACTION_OPTIONS;
	}, [readOnly, status, comment.reply]);

	const handleBlockUser = useCallback(() => {
		dispatch(
			showModal({
				id: "block-user",
				description: `Block ${comment.userName}? You won't see their comments in this or future broadcasts.`,
				onConfirm: () => {
					dispatch(
						blockCommentUserRequest({ broadcastId, commentId: comment.id }),
					);
				},
			}),
		);
	}, [dispatch, broadcastId, comment.id, comment.userName]);

	const handleMenuItemSelected = useCallback(
		(id: string) => {
			if (id === "reply") {
				setShowReplyBox(true);
			}

			if (id === "share") {
				handleShareToFeed();
			}

			if (id === "read") {
				handleMarkAsRead();
			}

			if (id === "unread") {
				handleMarkAsUnread();
			}

			if (id === "block") {
				handleBlockUser();
			}

			dispatch(setOpenMenu(null));
		},
		[
			dispatch,
			handleShareToFeed,
			handleMarkAsRead,
			handleMarkAsUnread,
			handleBlockUser,
		],
	);

	useEffect(() => {
		if (!isMenuOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (!menuRef.current?.contains(event.target as Node)) {
				dispatch(setOpenMenu(null));
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isMenuOpen, dispatch]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleReply();
			}
		},
		[handleReply],
	);

	return {
		showReplyBox,
		setShowReplyBox,
		replyText,
		setReplyText,
		commentTimeAgo,
		replyTimeAgo,
		isMenuOpen,
		menuRef,
		menuOptions,
		handleToggleMenu,
		handleMenuItemSelected,
		handleReply,
		handleKeyDown,
	};
};
